import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { mediaUrl, SITE_URL } from '../api/client';
import { stripHtml, truncate } from '../utils/format';

/**
 * Central SEO tag manager built on react-helmet.
 * Props: title, description, keywords, image, type, canonical, seo (entity seo object), noIndex, jsonLd (object|array), breadcrumbs
 */
export default function Seo({ title, description, keywords, image, type = 'website', canonical, seo = {}, noIndex, jsonLd, breadcrumbs, children }) {
  const { settings } = useSettings();
  const location = useLocation();
  const site = settings.seo || {};
  const siteName = settings.general?.siteName || 'ELEGEEK';
  const base = (site.siteUrl || SITE_URL || '').replace(/\/$/, '');

  const finalTitle = seo.metaTitle || (title ? `${title}${site.titleSuffix || ` – ${siteName}`}` : site.defaultTitle || siteName);
  const finalDesc = truncate(stripHtml(seo.metaDescription || description || site.defaultDescription || ''), 300);
  const finalKeywords = (seo.keywords && seo.keywords.length ? seo.keywords : keywords || site.defaultKeywords || []).join(', ');
  const ogImage = mediaUrl(seo.ogImage || image || site.defaultOgImage || '');
  const absImage = ogImage && ogImage.startsWith('/') ? `${base}${ogImage}` : ogImage;
  const url = seo.canonicalUrl || canonical || `${base}${location.pathname}`;
  const robots = seo.noIndex || noIndex ? `noindex, ${seo.noFollow ? 'nofollow' : 'follow'}` : seo.robots || 'index, follow';

  const ld = [];
  if (site.organizationSchema !== false) {
    ld.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteName,
      url: base,
      logo: `${base}${mediaUrl(settings.general?.logo || '/logo.png')}`,
      sameAs: Object.values(settings.social || {}).filter(Boolean),
      ...(settings.general?.phone ? { contactPoint: { '@type': 'ContactPoint', telephone: settings.general.phone, contactType: 'customer service' } } : {}),
    });
  }
  if (location.pathname === '/') {
    ld.push({ '@context': 'https://schema.org', '@type': 'WebSite', name: siteName, url: base, potentialAction: { '@type': 'SearchAction', target: `${base}/search?q={search_term_string}`, 'query-input': 'required name=search_term_string' } });
  }
  if (breadcrumbs && breadcrumbs.length) {
    ld.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({ '@type': 'ListItem', position: i + 1, name: b.label, item: `${base}${b.url || location.pathname}` })),
    });
  }
  if (jsonLd) (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach((x) => ld.push(x));
  if (seo.structuredData) {
    try {
      ld.push(JSON.parse(seo.structuredData));
    } catch (e) {
      /* ignore invalid custom JSON-LD */
    }
  }

  return (
    <Helmet>
      <html lang="en" />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={seo.ogType || type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={seo.ogTitle || finalTitle} />
      <meta property="og:description" content={seo.ogDescription || finalDesc} />
      {absImage && <meta property="og:image" content={absImage} />}
      <meta name="twitter:card" content={seo.twitterCard || 'summary_large_image'} />
      {site.twitterHandle && <meta name="twitter:site" content={site.twitterHandle} />}
      <meta name="twitter:title" content={seo.twitterTitle || seo.ogTitle || finalTitle} />
      <meta name="twitter:description" content={seo.twitterDescription || seo.ogDescription || finalDesc} />
      {(seo.twitterImage || absImage) && <meta name="twitter:image" content={mediaUrl(seo.twitterImage) || absImage} />}
      {site.googleSiteVerification && <meta name="google-site-verification" content={site.googleSiteVerification} />}
      {settings.general?.favicon && <link rel="icon" href={mediaUrl(settings.general.favicon)} />}
      {ld.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
      {children}
    </Helmet>
  );
}
